import React, { useState, useEffect } from "react";
import WhatsAppIcon from '@/components/icons/WhatsAppIcon';
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import ticketsService, { TicketUpdateData, CreateTicketData, type TicketSearchParams } from '@/services/tickets.service';
import usersService from '@/services/users.service';
import organizationsService from '@/services/organizations.service';
import frequentContactsService from '@/services/frequentContacts.service';
import customViewsService from '@/services/customViews.service';
import { useCustomView } from '@/hooks/useCustomViews';
import { useTags } from '@/hooks/useTags';
import { transformApiTicketToFrontend, transformApiCommentToFrontend, mapFrontendStatusToApi, Ticket, getPriorityLabel, getSlaStatusColor, getSlaStatusLabel, formatSlaTime, getStatusLabel } from '@/lib/ticketUtils';
import DOMPurify from 'dompurify';
import { ScrollArea } from "@/components/ui/scroll-area";
import useAuthStore from '@/stores/authStore';
import { cn } from '@/lib/utils';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Avatar } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ChevronDown,
  Clock,
  AlertCircle,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Shield,
  Ban,
  Merge,
  FilterIcon,
  GripVertical,
  MessageCircle,
  TriangleAlert,
  Plus,
  Send,
  Loader2,
  Paperclip,
  X,
  BookUser,
  Search,
  Settings,
  RotateCcw,
  Eye,
  EyeOff,
  UserPlus,
  RefreshCw,
  PanelRightClose,
  PanelRightOpen,
  ExternalLink,
} from "lucide-react";
import { formatDateTime, formatDayTime } from '@/lib/format';
import { useTranslation } from 'react-i18next';

const getStatusColor = (status: string) => {
  switch (status) {
    case "new":
      return "ticket-list-status ticket-list-status--new";
    case "open":
      return "ticket-list-status ticket-list-status--open";
    case "pending":
      return "ticket-list-status ticket-list-status--pending";
    case "in_progress":
      return "ticket-list-status ticket-list-status--progress";
    case "solved":
      return "ticket-list-status ticket-list-status--solved";
    case "closed":
      return "ticket-list-status ticket-list-status--closed";
    default:
      return "ticket-list-status ticket-list-status--closed";
  }
};

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'urgent': return 'ticket-list-priority ticket-list-priority--urgent';
    case 'high': return 'ticket-list-priority ticket-list-priority--high';
    case 'normal': return 'ticket-list-priority ticket-list-priority--normal';
    default: return 'ticket-list-priority ticket-list-priority--low';
  }
};

// Helper function to format ticket number - tam format göster
const formatTicketNumber = (ticketNumber?: string): string => {
  return ticketNumber || '';
};


interface TicketListProps {
  onTicketSelect?: (ticketId: string) => void;
  currentView?: string;
}

export type { TicketListProps };

interface OpenTicket {
  id: string;
  subject: string;
  status: "new" | "open" | "pending" | "in_progress" | "closed";
}

type SortField =
  | "id"
  | "status"
  | "priority"
  | "subject"
  | "requester"
  | "updatedAt"
  | "createdAt"
  | "dueDate"
  | "owner"
  | "watcher"
  | "messageCount"
  | "sla"
  | "channel"
  | "tags";
type SortDirection = "asc" | "desc";

type ColumnConfig = {
  key: SortField;
  labelKey?: string;
  label: string;
  width: number;
  visible: boolean;
};

const defaultColumns: ColumnConfig[] = [
  { key: "status", labelKey: "ticketList:column.status", label: "Status", width: 120, visible: true },
  { key: "priority", labelKey: "ticketList:column.priority", label: "Priorität", width: 100, visible: true },
  { key: "subject", labelKey: "ticketList:column.subject", label: "Betreff", width: 300, visible: true },
  { key: "requester", labelKey: "ticketList:column.requester", label: "Anfragender", width: 200, visible: true },
  { key: "owner", labelKey: "ticketList:column.owner", label: "Zugewiesen an", width: 150, visible: true },
  { key: "watcher", labelKey: "ticketList:column.watcher", label: "Beobachter", width: 180, visible: false },
  { key: "channel", labelKey: "ticketList:column.channel", label: "Kanal", width: 100, visible: true },
  { key: "tags", labelKey: "ticketList:column.tags", label: "Tags", width: 200, visible: true },
  { key: "messageCount", labelKey: "ticketList:column.messageCount", label: "Nachrichten", width: 100, visible: true },
  { key: "sla", labelKey: "ticketList:column.sla", label: "SLA Status", width: 160, visible: true },
  { key: "createdAt", labelKey: "ticketList:column.createdAt", label: "Erstellt am", width: 180, visible: true },
  { key: "updatedAt", labelKey: "ticketList:column.updatedAt", label: "Aktualisiert am", width: 180, visible: true },
  { key: "dueDate", labelKey: "ticketList:column.dueDate", label: "Fälligkeitsdatum", width: 160, visible: true },
];

const TicketList = ({ onTicketSelect, currentView }: TicketListProps = {}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const { viewId } = useParams<{ viewId: string }>();

  // Fetch custom view if viewing a custom view
  const { data: customViewData } = useCustomView(viewId || '');

  // Fetch tags for color mapping
  const { data: tagsData } = useTags();
  const tagColorMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    if (tagsData?.data?.tags) {
      tagsData.data.tags.forEach(tag => {
        map[tag.name] = tag.color;
      });
    }
    return map;
  }, [tagsData]);

  // Debug logging
  useEffect(() => {
    if (currentView === 'custom') {
      console.log('Custom view debug:', {
        currentView,
        viewId,
        hasCustomViewData: !!customViewData,
        customViewDataStructure: customViewData,
      });
    }
  }, [currentView, viewId, customViewData]);

  const [selectedTickets, setSelectedTickets] = useState<string[]>([]);
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  const [selectedParentTicketId, setSelectedParentTicketId] = useState<string | null>(null);

  // Preview panel state
  const [previewTicketId, setPreviewTicketId] = useState<string | null>(null);
  const [isPreviewEnabled, setIsPreviewEnabled] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('ticketList_previewEnabled');
      return saved === 'true';
    } catch {
      return false;
    }
  });

  // Multi-select status filter - load from localStorage or default to all
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('ticketStatusFilter');
      return saved ? JSON.parse(saved) : ['all'];
    } catch {
      return ['all'];
    }
  });

  // Get search params from URL if we're on search page
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('q') || '';
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState(false);
  const [searchDraft, setSearchDraft] = useState(searchQuery);
  const [debouncedSearchDraft, setDebouncedSearchDraft] = useState(searchQuery);
  const searchDialogInputRef = React.useRef<HTMLInputElement>(null);
  // Yorum içi arama backend tarafında FTS (content_tsv) ile her zaman açık;
  // burada ayrı bir flag/toggle'a gerek yok.

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearchDraft(searchDraft.trim()), 220);
    return () => window.clearTimeout(timer);
  }, [searchDraft]);

  useEffect(() => {
    const handleSearchShortcut = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchDraft(searchQuery);
        setIsSearchDialogOpen(true);
      }
    };
    window.addEventListener('keydown', handleSearchShortcut);
    return () => window.removeEventListener('keydown', handleSearchShortcut);
  }, [searchQuery]);

  useEffect(() => {
    if (!isSearchDialogOpen) return;

    const focusTimer = window.setTimeout(() => searchDialogInputRef.current?.focus(), 180);
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsSearchDialogOpen(false);
    };

    window.addEventListener('keydown', handleEscape);
    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isSearchDialogOpen]);

  // Save selected statuses to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('ticketStatusFilter', JSON.stringify(selectedStatuses));
    } catch (error) {
      console.warn('Failed to save status filter to localStorage:', error);
    }
  }, [selectedStatuses]);

  // Status filter helper functions
  const handleStatusToggle = (status: string) => {
    if (status === 'all') {
      setSelectedStatuses(['all']);
    } else {
      const newStatuses = selectedStatuses.includes('all')
        ? [status] // If 'all' was selected, clear it and add only this status
        : selectedStatuses.includes(status)
          ? selectedStatuses.filter(s => s !== status) // Remove if already selected
          : [...selectedStatuses, status]; // Add if not selected

      // If no statuses are selected, default to 'all'
      if (newStatuses.length === 0) {
        setSelectedStatuses(['all']);
      } else {
        setSelectedStatuses(newStatuses);
      }
    }
  };

  const getStatusDisplayText = () => {
    if (selectedStatuses.includes('all') || selectedStatuses.length === 0) {
      return 'Alle Status';
    }
    if (selectedStatuses.length === 1) {
      return getStatusLabel(selectedStatuses[0]);
    }
    return `${selectedStatuses.length} Status`;
  };

  const [ownerFilter, setOwnerFilter] = useState<string>("all");
  const [shopFilter, setShopFilter] = useState<string>(() => {
    return localStorage.getItem('ticketList_shopFilter') || "all";
  });
  const [ticketsPerPage, setTicketsPerPage] = useState<number>(() => {
    try {
      const saved = localStorage.getItem('ticketList_ticketsPerPage');
      return saved ? parseInt(saved, 10) : 50;
    } catch {
      return 50;
    }
  });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortField, setSortField] = useState<SortField>(() => {
    try {
      const saved = localStorage.getItem('ticketList_sortField');
      return (saved as SortField) || "updatedAt";
    } catch {
      return "updatedAt";
    }
  });
  const [sortDirection, setSortDirection] = useState<SortDirection>(() => {
    try {
      const saved = localStorage.getItem('ticketList_sortDirection');
      return (saved as SortDirection) || "desc";
    } catch {
      return "desc";
    }
  });
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [spamDialogOpen, setSpamDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<string>("");
  const [newAssignee, setNewAssignee] = useState<string>("");
  const [columns, setColumns] = useState<ColumnConfig[]>(() => {
    try {
      const saved = localStorage.getItem('ticketListColumns');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Merge saved settings with defaults to handle new columns.
        // WICHTIG: nur width/visible aus dem Speicher übernehmen — `label` kommt IMMER
        // aus defaultColumns (sonst würden alte, gespeicherte Beschriftungen die
        // übersetzten Spaltentitel überschreiben).
        return defaultColumns.map(defCol => {
          const savedCol = parsed.find((s: Partial<ColumnConfig>) => s.key === defCol.key);
          return savedCol
            ? {
                ...defCol,
                width: typeof savedCol.width === 'number' ? savedCol.width : defCol.width,
                visible: typeof savedCol.visible === 'boolean' ? savedCol.visible : defCol.visible,
              }
            : defCol;
        });
      }
      return defaultColumns;
    } catch {
      return defaultColumns;
    }
  });
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);
  const [showTicketNumber, setShowTicketNumber] = useState(() => {
    try {
      const saved = localStorage.getItem('ticketListShowTicketNumber');
      return saved !== null ? saved === 'true' : true;
    } catch {
      return true;
    }
  });
  const [showOrganization, setShowOrganization] = useState(() => {
    try {
      const saved = localStorage.getItem('ticketListShowOrganization');
      return saved !== null ? saved === 'true' : true;
    } catch {
      return true;
    }
  });

  // Save column settings to localStorage whenever they change
  // NUR key/width/visible speichern — `label` ist übersetzbar und darf nie persistiert werden.
  useEffect(() => {
    try {
      localStorage.setItem(
        'ticketListColumns',
        JSON.stringify(columns.map(({ key, width, visible }) => ({ key, width, visible })))
      );
    } catch (error) {
      console.warn('Failed to save column settings to localStorage:', error);
    }
  }, [columns]);

  // Save showTicketNumber to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('ticketListShowTicketNumber', String(showTicketNumber));
    } catch (error) {
      console.warn('Failed to save showTicketNumber to localStorage:', error);
    }
  }, [showTicketNumber]);

  // Save showOrganization to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('ticketListShowOrganization', String(showOrganization));
    } catch (error) {
      console.warn('Failed to save showOrganization to localStorage:', error);
    }
  }, [showOrganization]);

  // Column visibility toggle
  const toggleColumnVisibility = (key: SortField) => {
    setColumns(prev => prev.map(col =>
      col.key === key ? { ...col, visible: !col.visible } : col
    ));
  };

  // Reset columns to default
  const resetColumns = () => {
    setColumns(defaultColumns);
    setColumnSettingsOpen(false);
  };

  const [draggedColumn, setDraggedColumn] = useState<number | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<number | null>(null);
  const [resizingColumn, setResizingColumn] = useState<number | null>(null);
  const [startX, setStartX] = useState<number>(0);
  const [startWidth, setStartWidth] = useState<number>(0);
  const [openTickets, setOpenTickets] = useState<OpenTicket[]>([
    {
      id: "TKT-1001",
      subject: "Wo ist meine Bestellung? #12346",
      status: "open",
    },
    {
      id: "TKT-1002",
      subject: "Defektes Produkt erhalten #12347",
      status: "pending",
    },
    {
      id: "TKT-1003",
      subject: "Rückerstattung für Bestellung #12348",
      status: "new",
    },
  ]);
  const [activeTab, setActiveTab] = useState<string | null>("TKT-1001");

  // Create ticket modal state
  const [isCreateTicketOpen, setIsCreateTicketOpen] = useState(false);
  const [ticketForm, setTicketForm] = useState({
    email: "",
    customerName: "",
    subject: "",
    priority: "normal" as "low" | "normal" | "high" | "urgent",
    organizationId: "",
  });
  // Interne Konversation (org-içi, email'siz) modu
  const [ticketMode, setTicketMode] = useState<'extern' | 'intern'>('extern');
  const [internalTargetOrgId, setInternalTargetOrgId] = useState('');
  const [internalMessage, setInternalMessage] = useState('');

  // Frequent contacts state
  const [isContactsPopoverOpen, setIsContactsPopoverOpen] = useState(false);
  const [contactSearchQuery, setContactSearchQuery] = useState('');

  // Fetch frequent contacts sorted by ticket count (most used first)
  const { data: contactsData } = useQuery({
    queryKey: ['frequentContactsTop'],
    queryFn: () => frequentContactsService.getTopContacts(),
  });

  // Filter contacts based on search
  const filteredContacts = contactsData?.data.filter(contact =>
    contact.name.toLowerCase().includes(contactSearchQuery.toLowerCase()) ||
    contact.email.toLowerCase().includes(contactSearchQuery.toLowerCase())
  ) || [];

  // Read URL query parameters
  const urlAssigneeId = searchParams.get('assigneeId');
  const urlStatus = searchParams.get('status');
  const urlOrganizationId = searchParams.get('organizationId');
  const urlSortBy = searchParams.get('sortBy');
  const urlSortOrder = searchParams.get('sortOrder');

  // Map frontend sort fields to backend field names
  const mapSortFieldToBackend = (field: SortField): string => {
    const mapping: Record<SortField, string> = {
      id: 'id',
      status: 'status',
      priority: 'priority',
      subject: 'subject',
      requester: 'requester',
      updatedAt: 'updated_at',
      createdAt: 'created_at',
      dueDate: 'due_date',
      owner: 'assignee',
      watcher: 'updated_at',
      messageCount: 'message_count',
      sla: 'sla_status',
      channel: 'channel',
      tags: 'tags'
    };
    return mapping[field] || 'updated_at';
  };

  // Fetch tickets from API with all filters
  const { data: ticketListResponse, isLoading, error, refetch } = useQuery({
    queryKey: ['tickets', currentPage, ticketsPerPage, selectedStatuses, ownerFilter, shopFilter, currentView, searchQuery, urlAssigneeId, urlStatus, urlOrganizationId, sortField, sortDirection, viewId, customViewData?.data?.view?.id],
    enabled: (currentView !== 'custom' || !!customViewData?.data?.view)
      // Search view'da min 2 karakter bekle (gereksiz ağır sorguyu önle)
      && !(currentView === 'search' && searchQuery.trim().length < 2),
    queryFn: ({ signal }) => {
      // If we're viewing a custom view, build query from filter config
      if (currentView === 'custom' && customViewData?.data?.view) {
        const view = customViewData.data.view;

        // Use user's current sort settings instead of view's saved sort_config
        const currentSortConfig = {
          sortBy: mapSortFieldToBackend(sortField),
          sortOrder: sortDirection
        };

        const queryString = customViewsService.buildQueryString(
          view.filter_config,
          currentSortConfig
        );

        console.log('Custom view query building:', {
          filterConfig: view.filter_config,
          sortConfig: currentSortConfig,
          queryString,
        });

        // Parse query string into params object
        const urlParams = new URLSearchParams(queryString);
        const params: any = {
          page: currentPage,
          limit: ticketsPerPage,
        };

        // Add all custom view filters to params
        urlParams.forEach((value, key) => {
          params[key] = value;
        });

        // Ensure sort params are set (override any from queryString)
        params.sortBy = mapSortFieldToBackend(sortField);
        params.sortOrder = sortDirection;

        console.log('Final params for custom view:', params);

        return ticketsService.getTickets(params);
      }

      // If we're in search view, use search endpoint
      if (currentView === 'search' && searchQuery.trim()) {
        return ticketsService.searchTickets({
          q: searchQuery,
          page: currentPage,
          limit: ticketsPerPage,
          sortBy: mapSortFieldToBackend(sortField) as TicketSearchParams['sortBy'],
          sortOrder: sortDirection,
          signal, // yarış iptali — yeni sorgu geldiğinde önceki istek kesilir
        });
      }

      // Prepare common params
      const params: any = {
        page: currentPage,
        limit: ticketsPerPage,
      };

      // URL parameters take precedence over state filters
      if (urlStatus) {
        params.status = urlStatus.split(',');
      } else if (!selectedStatuses.includes('all') && selectedStatuses.length > 0) {
        params.status = selectedStatuses;
      }

      if (urlAssigneeId) {
        params.assigneeId = urlAssigneeId;
      } else if (currentView !== 'spam' && ownerFilter !== 'all') {
        if (ownerFilter === 'unassigned') {
          params.assigneeId = 'unassigned';
        } else {
          params.assigneeId = ownerFilter;
        }
      }

      if (urlOrganizationId) {
        params.organizationId = urlOrganizationId;
      } else if (shopFilter !== 'all') {
        params.organizationId = shopFilter;
      }

      // Add sorting parameters - use URL params if available, otherwise use local state
      if (urlSortBy) {
        params.sortBy = urlSortBy;
      } else {
        params.sortBy = mapSortFieldToBackend(sortField);
      }
      if (urlSortOrder) {
        params.sortOrder = urlSortOrder;
      } else {
        params.sortOrder = sortDirection;
      }

      // If we're in spam view, use spam endpoint
      if (currentView === 'spam') {
        return ticketsService.getSpamTickets(params);
      }

      // Social-Kommentare: eigener Posteingang. Diese Tickets sind aus der
      // normalen Liste ausgeschlossen und kommen nur über diesen Filter.
      if (currentView === 'comments') {
        params.ticketType = 'social_comment';
      }

      // Instagram & Messenger: Direktnachrichten in eigener Liste
      if (currentView === 'social') {
        params.channel = 'instagram,messenger';
      }

      // Otherwise use regular ticket list endpoint
      return ticketsService.getTickets(params);
    },
    staleTime: 0, // Always fetch fresh data - cache disabled
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes for back navigation
  });

  const { data: quickSearchResponse, isFetching: isQuickSearching } = useQuery({
    queryKey: ['ticket-list-quick-search', debouncedSearchDraft],
    queryFn: () => ticketsService.searchTickets({
      q: debouncedSearchDraft,
      page: 1,
      limit: 8,
      mode: 'autocomplete',
    }),
    enabled: isSearchDialogOpen && debouncedSearchDraft.length >= 2,
    staleTime: 15 * 1000,
  });

  const quickSearchTickets = React.useMemo(
    () => quickSearchResponse?.data?.tickets?.map(transformApiTicketToFrontend) || [],
    [quickSearchResponse],
  );

  // Fetch agents list for owner filter
  const { data: agentsResponse } = useQuery({
    queryKey: ['agents-list'],
    queryFn: () => usersService.getAgents(false, 'active'),
  });

  // Fetch organizations (shops) for shop filter
  const { data: orgsResponse } = useQuery({
    queryKey: ['organizations-list'],
    queryFn: () => organizationsService.getOrganizations(),
    staleTime: 10 * 60 * 1000,
  });

  // Fetch preview ticket details
  const { data: previewTicketData, isLoading: isPreviewLoading } = useQuery({
    queryKey: ['ticket-preview', previewTicketId],
    queryFn: () => ticketsService.getTicketById(previewTicketId!),
    enabled: !!previewTicketId && isPreviewEnabled,
    staleTime: 30 * 1000, // 30 seconds
  });

  // Mutations for bulk operations
  const statusUpdateMutation = useMutation({
    mutationFn: ({ ticketIds, data }: { ticketIds: string[], data: TicketUpdateData }) =>
      ticketsService.bulkUpdateTickets(ticketIds, data),
    onSuccess: (_, { ticketIds }) => {
      console.log('✅ Bulk assignment completed successfully');
      // Invalidate ticket list cache
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['tickets-stats'] });

      // Invalidate individual ticket caches
      ticketIds.forEach(ticketId => {
        queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
      });

      setSelectedTickets([]);
    },
    onError: (error: any) => {
      console.error('❌ Bulk assignment failed:', error);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (ticketIds: string[]) => ticketsService.deleteTickets(ticketIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['tickets-stats'] });
      setSelectedTickets([]);
      setDeleteDialogOpen(false); // Modal'ı başarı durumunda kapat
      console.log(`${selectedTickets.length} tickets successfully deleted`);
    },
    onError: (error: any) => {
      console.error('Failed to delete tickets:', error);
      const errorMessage = error?.response?.data?.message || 'Tickets konnten nicht gelöscht werden';
      alert(`Fehler: ${errorMessage}`);
      setDeleteDialogOpen(false); // Modal'ı hata durumunda da kapat
    },
  });

  const spamMutation = useMutation({
    mutationFn: (ticketIds: string[]) => ticketsService.markAsSpam(ticketIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      setSelectedTickets([]);
    },
  });

  const mergeMutation = useMutation({
    mutationFn: ({ parentTicketId, childTicketIds }: { parentTicketId: string; childTicketIds: string[] }) =>
      ticketsService.mergeTickets(parentTicketId, childTicketIds),
    onSuccess: (response) => {
      console.log('✅ Tickets merged successfully:', response.data);
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['tickets-stats'] });
      setSelectedTickets([]);
      setIsMergeModalOpen(false);
      setSelectedParentTicketId(null);
      alert(`✅ ${response.data.mergedTickets.length} Ticket(s) erfolgreich zusammengeführt!`);
    },
    onError: (error: any) => {
      console.error('❌ Merge failed:', error);
      const errorMessage = error?.response?.data?.message || 'Tickets konnten nicht zusammengeführt werden';
      alert(`Fehler: ${errorMessage}`);
    },
  });

  // Create ticket mutation
  const createTicketMutation = useMutation({
    mutationFn: (data: CreateTicketData) => ticketsService.createTicket(data),
    onSuccess: (response) => {
      console.log('✅ Ticket created successfully:', response.data.ticket.ticket_number);
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['tickets-stats'] });
      setTicketForm({
        email: "",
        customerName: "",
        subject: "",
        priority: "normal",
        organizationId: "",
      });
      setIsCreateTicketOpen(false);
      navigate(`/tickets/${response.data.ticket.id}`);
    },
    onError: (error: any) => {
      console.error('❌ Failed to create ticket:', error);
      const errorMessage = error?.response?.data?.message || 'Ticket konnte nicht erstellt werden';
      alert(`Fehler: ${errorMessage}`);
    }
  });


  // Transform API tickets to frontend format
  // Search view için de aynı useQuery sonucu kullanılır — duplicate fetch kaldırıldı
  const tickets: Ticket[] = ticketListResponse?.data?.tickets?.map(transformApiTicketToFrontend) || [];
  const pagination = ticketListResponse?.data?.pagination;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedTickets(tickets.map((ticket) => ticket.id));
    } else {
      setSelectedTickets([]);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear().toString().slice(-2);
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");

    return `${day}.${month}.${year} ${hours}:${minutes}`;
  };

  const formatDateOnly = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear().toString().slice(-2);

    return `${day}.${month}.${year}`;
  };

  const handleStatusChange = () => {
    if (newStatus && selectedTickets.length > 0) {
      const apiStatus = mapFrontendStatusToApi(newStatus as Ticket['status']);
      console.log('🎯 Bulk status update triggered:', { selectedTickets, newStatus, apiStatus });
      statusUpdateMutation.mutate({
        ticketIds: selectedTickets,
        data: { status: apiStatus as any }
      });
      setStatusDialogOpen(false);
      setNewStatus("");
    }
  };

  const handleAssignChange = () => {
    if (newAssignee && selectedTickets.length > 0) {
      console.log('🎯 Bulk assignment triggered:', {
        selectedTickets,
        newAssignee,
        assigneeId: newAssignee === 'unassigned' ? null : newAssignee
      });
      statusUpdateMutation.mutate({
        ticketIds: selectedTickets,
        data: { assigneeId: newAssignee === 'unassigned' ? null : newAssignee }
      });
      setAssignDialogOpen(false);
      setNewAssignee("");
    }
  };

  const handleDelete = () => {
    if (selectedTickets.length > 0) {
      deleteMutation.mutate(selectedTickets);
      // Modal'ı hemen kapatma - mutation success/error'da kapanacak
    }
  };

  const handleMarkAsSpam = () => {
    if (selectedTickets.length > 0) {
      spamMutation.mutate(selectedTickets);
      setSpamDialogOpen(false);
    }
  };

  const handleTicketFormChange = (field: string, value: string): void => {
    setTicketForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSendTicket = (): void => {
    if (ticketMode === 'intern') {
      if (!ticketForm.subject || !ticketForm.organizationId || !internalTargetOrgId) {
        alert(t('common:x2.provideSubjectAndOrgs', 'Bitte Betreff, Quell- und Ziel-Organisation angeben'));
        return;
      }
      if (internalTargetOrgId === ticketForm.organizationId) {
        alert(t('common:x2.targetOrgMustDiffer', 'Ziel-Organisation muss eine andere sein'));
        return;
      }
      createTicketMutation.mutate({
        internalTargetOrgId,
        subject: ticketForm.subject,
        content: internalMessage || undefined,
        priority: ticketForm.priority,
        organizationId: ticketForm.organizationId,
        sendEmail: false,
      } as any);
      return;
    }
    if (!ticketForm.email || !ticketForm.subject || !ticketForm.organizationId) {
      alert(t('views:u.mFillRequired', 'Bitte füllen Sie alle erforderlichen Felder aus'));
      return;
    }

    const payload = {
      customerEmail: ticketForm.email,
      customerName: ticketForm.customerName,
      subject: ticketForm.subject,
      priority: ticketForm.priority,
      organizationId: ticketForm.organizationId,
    };

    console.log('📤 Creating ticket:', payload);

    createTicketMutation.mutate(payload);
  };

  const handleCancelTicket = (): void => {
    setTicketForm({
      email: "",
      customerName: "",
      subject: "",
      priority: "normal",
      organizationId: "",
    });
    setIsCreateTicketOpen(false);
  };


  const handleColumnDragStart = (e: React.DragEvent, index: number) => {
    setDraggedColumn(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleColumnDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverColumn(index);
  };

  const handleColumnDragEnd = () => {
    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  const handleColumnDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedColumn === null || draggedColumn === dropIndex) return;

    const newColumns = [...columns];
    const draggedItem = newColumns[draggedColumn];
    newColumns.splice(draggedColumn, 1);
    newColumns.splice(dropIndex, 0, draggedItem);
    setColumns(newColumns);
    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  const handleResizeStart = (e: React.MouseEvent, columnIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    setResizingColumn(columnIndex);
    setStartX(e.clientX);
    setStartWidth(columns[columnIndex].width);
  };

  const handleResizeMove = (e: MouseEvent) => {
    if (resizingColumn === null) return;

    const diff = e.clientX - startX;
    const newWidth = Math.max(80, startWidth + diff);

    const newColumns = [...columns];
    newColumns[resizingColumn] = {
      ...newColumns[resizingColumn],
      width: newWidth,
    };
    setColumns(newColumns);
  };

  const handleResizeEnd = () => {
    setResizingColumn(null);
  };

  React.useEffect(() => {
    if (resizingColumn !== null) {
      document.addEventListener("mousemove", handleResizeMove);
      document.addEventListener("mouseup", handleResizeEnd);
      return () => {
        document.removeEventListener("mousemove", handleResizeMove);
        document.removeEventListener("mouseup", handleResizeEnd);
      };
    }
  }, [resizingColumn, startX, startWidth]);

  const handleSelectTicket = (ticketId: string, checked: boolean) => {
    if (checked) {
      setSelectedTickets([...selectedTickets, ticketId]);
    } else {
      setSelectedTickets(selectedTickets.filter((id) => id !== ticketId));
    }
  };

  const handleTicketClick = (ticketId: string, listIndex?: number, ticketNumber?: string) => {
    if (isPreviewEnabled) {
      setPreviewTicketId(ticketId);
    } else {
      // Calculate actual position in the full list (considering pagination)
      const listPosition = listIndex !== undefined
        ? ((currentPage - 1) * ticketsPerPage) + listIndex + 1
        : undefined;

      // Get next ticket info (based on current sorting)
      const nextTicket = listIndex !== undefined && listIndex < displayedTickets.length - 1
        ? displayedTickets[listIndex + 1]
        : null;

      // Store view context in sessionStorage for clean URLs across navigations
      const viewContext = {
        fromViewId: viewId,
        fromViewName: customViewData?.data?.view?.name,
        sortField,
        sortDirection,
        listPosition,
        totalTickets: pagination?.total,
        viewFilterConfig: customViewData?.data?.view?.filter_config
      };
      sessionStorage.setItem('ticketViewContext', JSON.stringify(viewContext));

      // Also pass in navigation state as fallback
      navigate(`/tickets/${ticketId}`, { state: viewContext });
    }
  };

  const openSearchDialog = () => {
    setSearchDraft(searchQuery);
    setIsSearchDialogOpen(true);
  };

  const submitSearch = () => {
    const query = searchDraft.trim();
    if (query.length < 2) return;
    setIsSearchDialogOpen(false);
    navigate(`/tickets/search?q=${encodeURIComponent(query)}`);
  };

  const openSearchTicket = (ticketId: string) => {
    setIsSearchDialogOpen(false);
    navigate(`/tickets/${ticketId}`);
  };

  // Toggle preview mode
  const togglePreviewMode = () => {
    const newValue = !isPreviewEnabled;
    setIsPreviewEnabled(newValue);
    localStorage.setItem('ticketList_previewEnabled', newValue.toString());
    if (!newValue) {
      setPreviewTicketId(null);
    }
  };

  // Open ticket in full view (from preview)
  const openTicketFullView = () => {
    if (previewTicketId) {
      const viewContext = {
        fromViewId: viewId,
        fromViewName: customViewData?.data?.view?.name,
        sortField,
        sortDirection,
        totalTickets: pagination?.total,
        viewFilterConfig: customViewData?.data?.view?.filter_config
      };
      sessionStorage.setItem('ticketViewContext', JSON.stringify(viewContext));
      navigate(`/tickets/${previewTicketId}`, { state: viewContext });
    }
  };

  const handleCloseTab = (ticketId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenTickets(openTickets.filter((t) => t.id !== ticketId));
    if (activeTab === ticketId) {
      const remainingTabs = openTickets.filter((t) => t.id !== ticketId);
      setActiveTab(
        remainingTabs.length > 0
          ? remainingTabs[remainingTabs.length - 1].id
          : null,
      );
    }
  };


  const handleSort = (field: SortField) => {
    let newDirection: SortDirection;
    if (sortField === field) {
      newDirection = sortDirection === "asc" ? "desc" : "asc";
      setSortDirection(newDirection);
    } else {
      newDirection = "asc";
      setSortField(field);
      setSortDirection(newDirection);
      localStorage.setItem('ticketList_sortField', field);
    }
    localStorage.setItem('ticketList_sortDirection', newDirection);
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" ? (
      <ChevronUp className="h-4 w-4" />
    ) : (
      <ChevronDown className="h-4 w-4" />
    );
  };

  // Backend handles sorting - no local sorting needed
  // Tickets come pre-sorted from the API based on sortField and sortDirection
  const displayedTickets = tickets;
  const totalPages = pagination?.totalPages || 1;
  const startIndex = pagination ? (pagination.page - 1) * pagination.limit + 1 : 1;
  const endIndex = pagination ? Math.min(pagination.page * pagination.limit, pagination.total) : displayedTickets.length;

  // Reset to first page when filters or sort change
  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatuses, ownerFilter, shopFilter, sortField, sortDirection]);

  // Mobile view - simple row (no nested cards)
  const renderTicketCard = (ticket: Ticket, index: number) => (
    <div
      key={ticket.id}
      className="ticket-list-mobile-card border-b border-gray-200 py-3 px-1 cursor-pointer"
      onClick={() => handleTicketClick(ticket.id, index, ticket.ticketNumber)}
    >
      <div className="flex justify-between items-start mb-1">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Checkbox
              checked={selectedTickets.includes(ticket.id)}
              onCheckedChange={(checked) =>
                handleSelectTicket(ticket.id, checked === true)
              }
              className="mr-1"
            />
            <span
              className={`text-[11px] font-medium px-2 py-0.5 rounded-md border ${getStatusColor(ticket.status)} border-current/20`}
            >
              {getStatusLabel(ticket.status)}
            </span>
            <Badge variant="outline" className="rounded-md text-[11px] px-2 py-0.5 border-gray-300 text-slate-700 font-medium">
              {ticket.organization.name}
            </Badge>
            {ticket.slaAlert && (
              <span className="text-[11px] text-red-600 font-medium">SLA</span>
            )}
          </div>
          <div className="text-[13px] font-medium text-slate-800">
            {ticket.subject}
          </div>
          <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-0.5 font-medium">
            <span>{formatTicketNumber(ticket.ticketNumber)}</span>
            {ticket.mergedTicketCount && ticket.mergedTicketCount > 0 && ticket.mergedTicketNumbers && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Badge className="bg-orange-100 text-orange-700 text-[10px] px-1.5 py-0 h-4 flex items-center gap-1">
                      <Merge className="h-3 w-3" />
                      {ticket.mergedTicketNumbers.map(num => formatTicketNumber(num)).join(', ')}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{ticket.mergedTicketCount} zusammengeführte Tickets</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
      </div>

      <div className="mt-2">
        <span className="text-[12px] font-medium text-slate-700">{ticket.requester.name}</span>
      </div>

      <div className="flex justify-between items-center text-[12px] mt-2">
        {ticket.owner ? (
          <Badge
            variant="secondary"
            className="px-2 py-1 text-xs bg-blue-100 text-blue-800 border-blue-200 rounded-md"
          >
            {ticket.owner.name}
          </Badge>
        ) : (
          <Badge
            variant="outline"
            className="px-2 py-1 text-xs text-gray-500 border-gray-300 rounded-md"
          >
            {t('ticket:ph.unassigned', 'Nicht zugewiesen')}
          </Badge>
        )}
      </div>
    </div>
  );

  return (
    <TooltipProvider>
      <div className="ticket-list-shell bg-white h-full flex flex-col">
        {/* Open Tickets Tabs and Create Button - hidden per request */}
        <div className="hidden" aria-hidden="true"></div>

        {/* Kopfzeile (Titel + Kennzahlen) entfernt: die Liste soll direkt oben
            beginnen. Ansichtsname steht in der Seitenleiste, die Anzahl in der
            Werkzeugleiste ("1-50 von N"). */}

        {/* Filter bar */}
        <div className="ticket-list-toolbar">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 flex-shrink min-w-0">
              <div className="ticket-list-filters">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="ticket-list-filter-trigger">
                      <FilterIcon className="h-3.5 w-3.5" />
                      <span>{getStatusDisplayText()}</span>
                      <ChevronDown className="h-3 w-3 opacity-60" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="ticket-list-filter-popover w-52 p-2">
                    {['all', 'new', 'open', 'pending', 'in_progress', 'solved', 'closed'].map((status) => (
                      <button
                        key={status}
                        type="button"
                        onClick={() => handleStatusToggle(status)}
                        className={cn(
                          "ticket-list-filter-option",
                          selectedStatuses.includes(status) && "is-active",
                        )}
                      >
                        <span>{status === 'all' ? 'Alle Status' : getStatusLabel(status)}</span>
                        {selectedStatuses.includes(status) && <span>✓</span>}
                      </button>
                    ))}
                  </PopoverContent>
                </Popover>

                <Select value={ownerFilter} onValueChange={setOwnerFilter}>
                  <SelectTrigger className="ticket-list-filter-trigger ticket-list-filter-select w-[150px]">
                    <SelectValue placeholder="Alle Agenten" />
                  </SelectTrigger>
                  <SelectContent className="ticket-list-select-content">
                    <SelectItem value="all">Alle Agenten</SelectItem>
                    <SelectItem value="unassigned">Nicht zugewiesen</SelectItem>
                    {agentsResponse?.data?.agents?.map((agent) => (
                      <SelectItem key={agent.id} value={agent.id}>{agent.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={shopFilter}
                  onValueChange={(value) => {
                    setShopFilter(value);
                    localStorage.setItem('ticketList_shopFilter', value);
                  }}
                >
                  <SelectTrigger className="ticket-list-filter-trigger ticket-list-filter-select w-[175px]">
                    <SelectValue placeholder="Alle Organisationen" />
                  </SelectTrigger>
                  <SelectContent className="ticket-list-select-content">
                    <SelectItem value="all">Alle Organisationen</SelectItem>
                    {orgsResponse?.data?.map((organization) => (
                      <SelectItem key={organization.id} value={organization.id}>{organization.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Collapsible Tools Menu */}
              <div className="ticket-list-bulk-actions flex items-center gap-1.5">
                <div className="group">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={selectedTickets.length === 0}
                    onClick={() => setAssignDialogOpen(true)}
                    className="rounded-full border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 shadow-sm transition-all duration-300 disabled:opacity-50 group-hover:border-rose-200 group-hover:text-rose-600 group-hover:px-3 whitespace-nowrap"
                  >
                    <UserPlus className="h-3.5 w-3.5" />
                    <span className="max-w-0 overflow-hidden opacity-0 transition-all duration-300 group-hover:max-w-[100px] group-hover:opacity-100 group-hover:ml-1.5">{t('ticketList:action.assign', 'Zuweisen')}</span>
                  </Button>
                </div>

                <div className="group">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={selectedTickets.length === 0}
                    onClick={() => setStatusDialogOpen(true)}
                    className="rounded-full border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 shadow-sm transition-all duration-300 disabled:opacity-50 group-hover:border-rose-200 group-hover:text-rose-600 group-hover:px-3 whitespace-nowrap"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    <span className="max-w-0 overflow-hidden opacity-0 transition-all duration-300 group-hover:max-w-[100px] group-hover:opacity-100 group-hover:ml-1.5">{t('ticketList:action.changeStatus', 'Status ändern')}</span>
                  </Button>
                </div>

                <div className="group">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={selectedTickets.length < 2}
                    onClick={() => setIsMergeModalOpen(true)}
                    className="rounded-full border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 shadow-sm transition-all duration-300 disabled:opacity-50 group-hover:border-rose-200 group-hover:text-rose-600 group-hover:px-3 whitespace-nowrap"
                  >
                    <Merge className="h-3.5 w-3.5" />
                    <span className="max-w-0 overflow-hidden opacity-0 transition-all duration-300 group-hover:max-w-[150px] group-hover:opacity-100 group-hover:ml-1.5">{t('ticketList:action.merge', 'Zusammenführen')}</span>
                  </Button>
                </div>

                <div className="group">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={selectedTickets.length === 0 || spamMutation.isPending}
                    onClick={() => setSpamDialogOpen(true)}
                    className="rounded-full border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 shadow-sm transition-all duration-300 disabled:opacity-50 group-hover:border-red-200 group-hover:text-red-600 group-hover:px-3 whitespace-nowrap"
                  >
                    <Ban className="h-3.5 w-3.5" />
                    <span className="max-w-0 overflow-hidden opacity-0 transition-all duration-300 group-hover:max-w-[150px] group-hover:opacity-100 group-hover:ml-1.5">{t('ticketList:action.archive', 'Spam')}</span>
                  </Button>
                </div>

                {/* Preview Mode Toggle */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={togglePreviewMode}
                        className={cn(
                          "rounded-full border-slate-200 px-2.5 py-1.5 text-xs font-medium shadow-sm transition-all duration-300 whitespace-nowrap",
                          isPreviewEnabled
                            ? "border-rose-200 bg-rose-50 text-rose-600"
                            : "text-slate-600 hover:border-rose-200 hover:text-rose-600"
                        )}
                      >
                        {isPreviewEnabled ? (
                          <PanelRightClose className="h-3.5 w-3.5" />
                        ) : (
                          <PanelRightOpen className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{isPreviewEnabled ? 'Vorschau deaktivieren' : 'Vorschau aktivieren'}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              <Popover open={columnSettingsOpen} onOpenChange={setColumnSettingsOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="ticket-list-columns-trigger flex items-center gap-1.5 rounded-full border-slate-200 px-3 py-1.5 text-xs text-slate-600 shadow-sm hover:bg-slate-100 whitespace-nowrap"
                  >
                    <Settings className="h-3.5 w-3.5" />
                    {t('ticketList:tl.columns', 'Spalten')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="ticket-list-column-popover w-72 p-0 rounded-xl border border-slate-200/80 bg-white shadow-xl"
                  align="end"
                  sideOffset={8}
                  collisionPadding={16}
                >
                  {/* Fixed Header */}
                  <div className="sticky top-0 z-10 bg-white border-b border-slate-100 px-4 py-3 rounded-t-xl">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-slate-800">{t('ticketList:action.showColumns', 'Spalten anzeigen')}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={resetColumns}
                        className="h-6 px-2 text-[10px] text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full"
                      >
                        <RotateCcw className="mr-1 h-2.5 w-2.5" />
                        {t('common:x2.reset', 'Reset')}
                      </Button>
                    </div>
                  </div>

                  {/* Scrollable Content */}
                  <div
                    className="max-h-[280px] overflow-y-auto px-2 py-2"
                    style={{
                      scrollbarWidth: 'thin',
                      scrollbarColor: '#e2e8f0 transparent'
                    }}
                  >
                    <style dangerouslySetInnerHTML={{
                      __html: `
                      .spalten-scroll::-webkit-scrollbar {
                        width: 6px;
                      }
                      .spalten-scroll::-webkit-scrollbar-track {
                        background: transparent;
                        margin: 4px 0;
                      }
                      .spalten-scroll::-webkit-scrollbar-thumb {
                        background: #e2e8f0;
                        border-radius: 3px;
                      }
                      .spalten-scroll::-webkit-scrollbar-thumb:hover {
                        background: #cbd5e1;
                      }
                    `}} />
                    <div className="space-y-0.5 spalten-scroll">
                      {columns.map((column) => (
                        <label
                          key={column.key}
                          htmlFor={`col-${column.key}`}
                          className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors hover:bg-slate-50 group"
                        >
                          <Checkbox
                            id={`col-${column.key}`}
                            checked={column.visible}
                            onCheckedChange={() => toggleColumnVisibility(column.key)}
                            className="h-4 w-4 rounded border-slate-300 data-[state=checked]:border-rose-400 data-[state=checked]:bg-rose-500"
                          />
                          <span className="text-sm text-slate-600 group-hover:text-slate-900 flex-1">
                            {column.labelKey ? t(column.labelKey, column.label) : column.label}
                          </span>
                          {column.visible && (
                            <span className="text-[9px] font-medium text-rose-500 bg-rose-50 px-1.5 py-0.5 rounded-full">
                              {t('common:state.active', 'Aktiv')}
                            </span>
                          )}
                        </label>
                      ))}

                      {/* Betreff Sub-Options */}
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <div className="px-3 mb-2">
                          <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                            {t('common:x2.subjectDetails', 'Betreff Details')}
                          </span>
                        </div>
                        <label
                          htmlFor="show-ticket-number"
                          className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors hover:bg-slate-50 group"
                        >
                          <Checkbox
                            id="show-ticket-number"
                            checked={showTicketNumber}
                            onCheckedChange={(checked) => setShowTicketNumber(checked as boolean)}
                            className="h-4 w-4 rounded border-slate-300 data-[state=checked]:border-rose-400 data-[state=checked]:bg-rose-500"
                          />
                          <span className="text-sm text-slate-600 group-hover:text-slate-900 flex-1">
                            {t('common:x2.ticketNumber', 'Ticket-Nummer')}
                          </span>
                          {showTicketNumber && (
                            <span className="text-[9px] font-medium text-rose-500 bg-rose-50 px-1.5 py-0.5 rounded-full">
                              {t('common:state.active', 'Aktiv')}
                            </span>
                          )}
                        </label>
                        <label
                          htmlFor="show-organization"
                          className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors hover:bg-slate-50 group"
                        >
                          <Checkbox
                            id="show-organization"
                            checked={showOrganization}
                            onCheckedChange={(checked) => setShowOrganization(checked as boolean)}
                            className="h-4 w-4 rounded border-slate-300 data-[state=checked]:border-rose-400 data-[state=checked]:bg-rose-500"
                          />
                          <span className="text-sm text-slate-600 group-hover:text-slate-900 flex-1">
                            {t('common:field.organization', 'Organisation')}
                          </span>
                          {showOrganization && (
                            <span className="text-[9px] font-medium text-rose-500 bg-rose-50 px-1.5 py-0.5 rounded-full">
                              {t('common:state.active', 'Aktiv')}
                            </span>
                          )}
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Fixed Footer */}
                  <div className="sticky bottom-0 bg-slate-50/80 backdrop-blur-sm border-t border-slate-100 px-4 py-2.5 rounded-b-xl">
                    <div className="text-[10px] text-slate-400 text-center">
                      {columns.filter(c => c.visible).length} von {columns.length} Spalten sichtbar
                    </div>
                  </div>
                </PopoverContent>
              </Popover>

              {/* Pagination Info */}
              {!isLoading && pagination && (
                <div className="ticket-list-pagination flex items-center gap-2 border-l border-slate-200 pl-3 ml-1">
                  <span className="text-[11px] text-slate-500">
                    {startIndex}-{endIndex} von {pagination?.total || 0}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1 || isLoading}
                      className="h-6 w-6 p-0 rounded-full hover:bg-slate-100"
                    >
                      <ChevronLeft className="h-3.5 w-3.5" />
                    </Button>
                    <span className="text-[11px] text-slate-600 min-w-[60px] text-center">
                      {currentPage} / {totalPages}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages || isLoading || !pagination?.hasNext}
                      className="h-6 w-6 p-0 rounded-full hover:bg-slate-100"
                    >
                      <ChevronRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <Select
                    value={ticketsPerPage.toString()}
                    onValueChange={(value) => {
                      const newValue = Number(value);
                      setTicketsPerPage(newValue);
                      setCurrentPage(1);
                      localStorage.setItem('ticketList_ticketsPerPage', value);
                    }}
                  >
                    <SelectTrigger className="h-6 w-[55px] text-[10px] border-slate-200 rounded-md">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                      <SelectItem value="125">125</SelectItem>
                      <SelectItem value="150">150</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="ticket-list-toolbar-right">
              <button
                type="button"
                className="ticket-list-search-trigger"
                onClick={openSearchDialog}
                aria-label="Tickets durchsuchen"
              >
                <Search className="h-3.5 w-3.5" />
                <span>{searchQuery || 'Tickets durchsuchen'}</span>
                <kbd>⌘K</kbd>
              </button>

              <Button
                onClick={() => setIsCreateTicketOpen(true)}
                size="sm"
                className="ticket-list-new-button flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold whitespace-nowrap"
              >
                <Plus className="h-3.5 w-3.5" />
                {t('ticketList:tl.newTicket', 'Neues Ticket')}
              </Button>
            </div>
          </div>
        </div>

        {/* Split View Container */}
        <div className="flex flex-1 min-h-0 gap-0">
          {/* Ticket List Section */}
          <div className={cn(
            "flex flex-col transition-all duration-300",
            isPreviewEnabled && previewTicketId ? "w-1/2" : "w-full"
          )}>
            {/* Desktop view - Table */}
            <div className="ticket-list-table-wrap hidden md:flex flex-1 overflow-auto">
              <Table className="ticket-list-table w-full" style={{ tableLayout: 'fixed' }}>
              <TableHeader className="ticket-list-table-header border-b border-slate-200/60">
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-[42px] text-center bg-white border-b border-slate-200 sticky top-0 z-20 shadow-sm">
                    <div className="flex justify-center">
                      <Checkbox
                        checked={
                          selectedTickets.length === displayedTickets.length &&
                          displayedTickets.length > 0
                        }
                        onCheckedChange={handleSelectAll}
                        className="h-4 w-4 rounded-md border-slate-300 data-[state=checked]:border-rose-200 data-[state=checked]:bg-rose-500/90"
                      />
                    </div>
                  </TableHead>
                  {columns.filter(c => c.visible).map((column) => {
                    const originalIndex = columns.findIndex(c => c.key === column.key);
                    return (
                      <TableHead
                        key={column.key}
                        className={`relative select-none px-3 py-2.5 text-[11px] font-medium text-slate-600 transition-colors bg-white border-b border-slate-200 sticky top-0 z-20 ${dragOverColumn === originalIndex ? "bg-rose-50" : "hover:bg-slate-50"
                          }`}
                        style={{
                          width: `${column.width}px`,
                        }}
                        draggable
                        onDragStart={(e) => handleColumnDragStart(e, originalIndex)}
                        onDragOver={(e) => handleColumnDragOver(e, originalIndex)}
                        onDragEnd={handleColumnDragEnd}
                        onDrop={(e) => handleColumnDrop(e, originalIndex)}
                        onClick={() => handleSort(column.key)}
                        title={column.labelKey ? t(column.labelKey, column.label) : column.label}
                      >
                        <div className="flex items-center gap-1.5 whitespace-nowrap overflow-hidden">
                          <span className="truncate">{column.labelKey ? t(column.labelKey, column.label) : column.label}</span>
                          {getSortIcon(column.key)}
                        </div>
                        <div
                          className="absolute right-0 top-2 bottom-2 w-px cursor-col-resize rounded-full bg-slate-200 transition-all duration-200 hover:bg-rose-300"
                          onMouseDown={(e) => handleResizeStart(e, originalIndex)}
                        />
                      </TableHead>
                    );
                  })}
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayedTickets.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.filter(c => c.visible).length + 1}
                      className="text-center py-6 text-gray-500"
                    >
                      {t('common:x1.noTicketsFilter', 'Keine Tickets gefunden. Bitte passen Sie Ihre Filter an.')}
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedTickets.map((ticket, rowIndex) => (
                    <TableRow
                      key={ticket.id}
                      className={`ticket-list-row group relative cursor-pointer border-b border-slate-100/70 text-[13px] leading-tight transition-colors duration-200 ${selectedTickets.includes(ticket.id)
                        ? "bg-rose-50/70 shadow-[inset_3px_0_0_rgba(244,63,94,0.45)]"
                        : rowIndex % 2 === 0
                          ? "bg-white"
                          : "bg-slate-50/80 hover:bg-white"
                        }`}
                      onClick={(e) => {
                        // Don't trigger row click if checkbox was clicked
                        if (
                          (e.target as HTMLElement).closest(
                            'button[role="checkbox"]',
                          )
                        ) {
                          return;
                        }
                        handleTicketClick(ticket.id, rowIndex, ticket.ticketNumber);
                      }}
                    >
                      <TableCell className="py-3 text-center">
                        <div className="flex justify-center">
                          <Checkbox
                            checked={selectedTickets.includes(ticket.id)}
                            onCheckedChange={(checked) =>
                              handleSelectTicket(ticket.id, checked === true)
                            }
                            className="h-4 w-4 rounded-md border-slate-300 data-[state=checked]:border-rose-200 data-[state=checked]:bg-rose-500/90"
                          />
                        </div>
                      </TableCell>
                      {columns.filter(c => c.visible).map((column) => {
                        switch (column.key) {
                          case "status":
                            return (
                              <TableCell
                                key={column.key}
                                className="py-3 overflow-hidden"
                              >
                                <div className="flex items-center">
                                  <span
                                    className={`inline-flex items-center rounded-full border border-current/20 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide shadow-sm ${getStatusColor(ticket.status)}`}
                                  >
                                    {getStatusLabel(ticket.status)}
                                  </span>
                                </div>
                              </TableCell>
                            );
                          case "priority":
                            return (
                              <TableCell
                                key={column.key}
                                className="py-3 overflow-hidden"
                              >
                                <div className="flex items-center">
                                  <span
                                    className={`inline-flex items-center rounded-full border border-current/20 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide shadow-sm ${getPriorityColor(ticket.priority)}`}
                                  >
                                    {getPriorityLabel(ticket.priority)}
                                  </span>
                                </div>
                              </TableCell>
                            );
                          case "subject":
                            return (
                              <TableCell
                                key={column.key}
                                className="py-3 overflow-hidden"
                              >
                                <div className="overflow-hidden">
                                  <div className="flex items-center gap-1.5 font-medium text-[13px] text-slate-800 truncate">
                                    {ticket.ai_flag && (
                                      <span
                                        className={cn(
                                          "inline-block w-2 h-2 rounded-full shrink-0",
                                          ticket.ai_flag === 'green' && "bg-emerald-500",
                                          ticket.ai_flag === 'yellow' && "bg-amber-500",
                                          ticket.ai_flag === 'red' && "bg-red-500"
                                        )}
                                        title={`KI: ${ticket.ai_flag}`}
                                      />
                                    )}
                                    <span className="truncate">{ticket.subject}</span>
                                  </div>
                                  {(showTicketNumber || showOrganization || ticket.slaAlert || (ticket as any).internalTargetOrgId) && (
                                    <div className="mt-1 flex items-center gap-2 text-[11px] text-slate-500 overflow-hidden">
                                      {showTicketNumber && (
                                        <span className="font-medium text-slate-600 flex-shrink-0">{formatTicketNumber(ticket.ticketNumber)}</span>
                                      )}
                                      {(ticket as any).internalTargetOrgId && (
                                        <Badge variant="outline" className="rounded-full border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-600 flex-shrink-0">
                                          {t('ticket:ui.internalConv', 'Interne Konversation')}{(ticket as any).internalTargetOrgName ? ` · ${(ticket as any).internalTargetOrgName}` : ''}
                                        </Badge>
                                      )}
                                      {showOrganization && (
                                        <Badge variant="outline" className="rounded-full border-slate-200 bg-white/80 px-2 py-0.5 text-[10px] font-medium text-slate-600 truncate max-w-[120px]">
                                          {ticket.organization.name}
                                        </Badge>
                                      )}
                                      {ticket.slaAlert && (
                                        <div className="flex items-center gap-1 rounded-full bg-rose-100/80 px-2 py-0.5 text-rose-600 ring-1 ring-rose-200 flex-shrink-0">
                                          <AlertCircle className="h-3 w-3" />
                                          <span className="text-[10px] font-semibold">
                                            SLA
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </TableCell>
                            );
                          case "requester":
                            return (
                              <TableCell
                                key={column.key}
                                className="py-3 overflow-hidden"
                              >
                                <div className="overflow-hidden min-w-0">
                                  <div className="font-medium text-[13px] text-slate-800 truncate">
                                    {ticket.requester.name}
                                  </div>
                                  <div className="text-[11px] text-slate-500 truncate">
                                    {ticket.requester.email}
                                  </div>
                                </div>
                              </TableCell>
                            );
                          case "messageCount":
                            return (
                              <TableCell
                                key={column.key}
                                className="py-3 overflow-hidden"
                              >
                                <div className="inline-flex items-center gap-1.5 rounded-full bg-slate-100/80 px-2 py-0.5 text-slate-600 ring-1 ring-slate-200">
                                  <MessageCircle className="h-3 w-3 text-slate-500" />
                                  <span className="text-[11px] font-semibold">
                                    {ticket.messageCount}
                                  </span>
                                </div>
                              </TableCell>
                            );
                          case "createdAt":
                            return (
                              <TableCell
                                key={column.key}
                                className="py-3 text-[11px] text-slate-600 overflow-hidden whitespace-nowrap"
                              >
                                {formatDate(ticket.createdAt)}
                              </TableCell>
                            );
                          case "updatedAt":
                            return (
                              <TableCell
                                key={column.key}
                                className="py-3 text-[11px] text-slate-600 overflow-hidden whitespace-nowrap"
                              >
                                {formatDate(ticket.updatedAt)}
                              </TableCell>
                            );
                          case "dueDate":
                            return (
                              <TableCell
                                key={column.key}
                                className="py-3 text-[11px] text-slate-600 overflow-hidden whitespace-nowrap"
                              >
                                {ticket.dueDate ? (
                                  ticket.slaAlert ? (
                                    <span className="rounded-full bg-rose-100/80 px-2 py-0.5 text-[11px] font-semibold text-rose-600 ring-1 ring-rose-200">
                                      {formatDateOnly(ticket.dueDate)}
                                    </span>
                                  ) : (
                                    <span>{formatDateOnly(ticket.dueDate)}</span>
                                  )
                                ) : (
                                  "-"
                                )}
                              </TableCell>
                            );
                          case "owner":
                            return (
                              <TableCell
                                key={column.key}
                                className="py-3 overflow-hidden"
                              >
                                {ticket.owner ? (
                                  <Badge
                                    variant="secondary"
                                    className="ticket-list-owner rounded-full px-2 py-0.5 text-[11px] font-semibold truncate max-w-full"
                                  >
                                    {ticket.owner.name}
                                  </Badge>
                                ) : (
                                  <Badge
                                    variant="outline"
                                    className="rounded-full border-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-500"
                                  >
                                    {t('ticket:ph.unassigned', 'Nicht zugewiesen')}
                                  </Badge>
                                )}
                              </TableCell>
                            );
                          case "watcher":
                            return (
                              <TableCell
                                key={column.key}
                                className="py-3 overflow-hidden"
                              >
                                {Array.isArray((ticket as any).watchers) && (ticket as any).watchers.length > 0 ? (
                                  <div className="flex flex-wrap gap-1">
                                    {(ticket as any).watchers.slice(0, 2).map((w: string, i: number) => (
                                      <Badge
                                        key={i}
                                        variant="secondary"
                                        className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600 ring-1 ring-slate-200 truncate max-w-full"
                                      >
                                        {w}
                                      </Badge>
                                    ))}
                                    {(ticket as any).watchers.length > 2 && (
                                      <Badge
                                        variant="outline"
                                        className="rounded-full border-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-500"
                                      >
                                        +{(ticket as any).watchers.length - 2}
                                      </Badge>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-[11px] text-slate-400">—</span>
                                )}
                              </TableCell>
                            );
                          case "sla":
                            return (
                              <TableCell
                                key={column.key}
                                className="py-3 overflow-hidden"
                              >
                                {ticket.sla ? (
                                  <div className="flex flex-col gap-0.5 overflow-hidden">
                                    <div className="text-[11px] font-semibold text-slate-600 truncate">
                                      {ticket.sla.policyName}
                                    </div>
                                    {ticket.sla.firstResponseStatus && (
                                      <div className="flex items-center gap-1">
                                        <span className="text-[10px] text-slate-500">R:</span>
                                        <span
                                          className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${getSlaStatusColor(ticket.sla.firstResponseStatus)}`}
                                        >
                                          {getSlaStatusLabel(ticket.sla.firstResponseStatus)}
                                        </span>
                                      </div>
                                    )}
                                    {ticket.sla.resolutionStatus && (
                                      <div className="flex items-center gap-1">
                                        <span className="text-[10px] text-slate-500">L:</span>
                                        <span
                                          className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${getSlaStatusColor(ticket.sla.resolutionStatus)}`}
                                        >
                                          {getSlaStatusLabel(ticket.sla.resolutionStatus)}
                                        </span>
                                      </div>
                                    )}
                                    {ticket.sla.isPaused && (
                                      <span className="text-[10px] text-orange-600">⏸</span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-gray-500 text-[10px]">
                                    {t('ticketList:tl.noSla', 'Kein SLA')}
                                  </span>
                                )}
                              </TableCell>
                            );
                          case "channel":
                            return (
                              <TableCell
                                key={column.key}
                                className="py-3"
                              >
                                <span
                                  className={`ticket-list-channel inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                                    ticket.channel === 'vynform'
                                      ? 'ticket-list-channel--form'
                                      : ticket.channel === 'chatbot'
                                      ? 'ticket-list-channel--chat'
                                      : ticket.channel === 'whatsapp'
                                      ? 'ticket-list-channel--wa'
                                      : ticket.channel === 'messenger'
                                      ? 'ticket-list-channel--fb'
                                      : ticket.channel === 'instagram'
                                      ? 'ticket-list-channel--ig'
                                      : 'ticket-list-channel--mail'
                                  }`}
                                >
                                  {ticket.channel === 'whatsapp' && <WhatsAppIcon className="h-3 w-3" />}
                                  {ticket.channel === 'vynform' ? 'VynForm' : ticket.channel === 'chatbot' ? 'Chatbot' : ticket.channel === 'whatsapp' ? 'WhatsApp' : ticket.channel === 'messenger' ? 'Messenger' : ticket.channel === 'instagram' ? 'Instagram' : 'E-Mail'}
                                </span>
                              </TableCell>
                            );
                          case "tags":
                            return (
                              <TableCell
                                key={column.key}
                                className="py-3 overflow-hidden"
                              >
                                {ticket.tags && ticket.tags.length > 0 ? (
                                  <div className="flex flex-wrap gap-1">
                                    {ticket.tags.slice(0, 3).map((tag, idx) => {
                                      const tagColor = tagColorMap[tag] || '#6B7280';
                                      return (
                                        <span
                                          key={idx}
                                          className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium"
                                          style={{
                                            backgroundColor: `${tagColor}20`,
                                            color: tagColor,
                                            border: `1px solid ${tagColor}40`,
                                          }}
                                        >
                                          {tag}
                                        </span>
                                      );
                                    })}
                                    {ticket.tags.length > 3 && (
                                      <span className="text-[10px] text-slate-500">
                                        +{ticket.tags.length - 3}
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-gray-400 text-[10px]">-</span>
                                )}
                              </TableCell>
                            );
                          default:
                            return null;
                        }
                      })}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            </div>

            {/* Mobile view - Cards */}
            {/* Mobil: Karten randlos, gleicher Seitenabstand wie die Tabelle */}
            <div className="md:hidden flex-1 overflow-y-auto px-4">
              {displayedTickets.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {t('common:x1.noTicketsFilter', 'Keine Tickets gefunden. Bitte passen Sie Ihre Filter an.')}
                </div>
              ) : (
                displayedTickets.map(renderTicketCard)
              )}
            </div>
          </div>

          {/* Preview Panel */}
          {isPreviewEnabled && previewTicketId && (
            <div className="ticket-list-preview w-1/2 border-l border-slate-200 bg-white flex flex-col overflow-hidden">
              {/* Preview Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50/50">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('ticketList:action.preview', 'Vorschau')}</span>
                  {previewTicketData?.data?.ticket && (
                    <span className="text-sm font-medium text-slate-700 truncate">
                      {previewTicketData.data.ticket.ticket_number}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={openTicketFullView}
                          className="h-7 w-7 p-0 rounded-full hover:bg-slate-100"
                        >
                          <ExternalLink className="h-3.5 w-3.5 text-slate-500" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{t('ticketList:action.openTicket', 'Ticket öffnen')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPreviewTicketId(null)}
                    className="h-7 w-7 p-0 rounded-full hover:bg-slate-100"
                  >
                    <X className="h-3.5 w-3.5 text-slate-500" />
                  </Button>
                </div>
              </div>

              {/* Preview Content */}
              {isPreviewLoading ? (
                <div className="flex-1 flex items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : previewTicketData?.data?.ticket ? (
                <div className="flex-1 flex flex-col min-h-0">
                  {/* Ticket Info */}
                  <div className="px-4 py-3 border-b border-slate-100 bg-white">
                    <h3 className="text-sm font-semibold text-slate-800 line-clamp-2">
                      {previewTicketData.data.ticket.subject}
                    </h3>
                    <div className="flex items-center gap-2 mt-1.5">
                      <Badge className={cn(
                        "rounded-full px-2 py-0.5 text-[10px] font-medium",
                        previewTicketData.data.ticket.status === 'open' && "bg-green-100 text-green-700",
                        previewTicketData.data.ticket.status === 'pending' && "bg-yellow-100 text-yellow-700",
                        previewTicketData.data.ticket.status === 'closed' && "bg-gray-100 text-gray-700",
                        String(previewTicketData.data.ticket.status) === 'new' && "bg-blue-100 text-blue-700"
                      )}>
                        {getStatusLabel(previewTicketData.data.ticket.status)}
                      </Badge>
                      <span className="text-xs text-slate-500">
                        {previewTicketData.data.ticket.requester_first_name} {previewTicketData.data.ticket.requester_last_name}
                      </span>
                    </div>
                  </div>

                  {/* Conversation Messages */}
                  <ScrollArea className="flex-1">
                    <div className="p-4 space-y-4">
                      {previewTicketData.data.comments && previewTicketData.data.comments.length > 0 ? (
                        previewTicketData.data.comments.map((comment: any) => {
                          const isCustomer = comment.author_id === previewTicketData.data.ticket.requester_id;
                          return (
                            <div
                              key={comment.id}
                              className={cn(
                                "rounded-xl p-3",
                                isCustomer
                                  ? "bg-slate-100 mr-4"
                                  : "bg-rose-50 ml-4",
                                comment.is_internal && "bg-amber-50 border border-amber-200"
                              )}
                            >
                              <div className="flex items-center gap-2 mb-1.5">
                                <div className={cn(
                                  "h-6 w-6 rounded-full flex items-center justify-center text-xs font-semibold",
                                  isCustomer
                                    ? "bg-slate-200 text-slate-600"
                                    : "bg-rose-200 text-rose-700"
                                )}>
                                  {(comment.author_first_name || 'U').charAt(0).toUpperCase()}
                                </div>
                                <span className="text-xs font-medium text-slate-700">
                                  {comment.author_first_name} {comment.author_last_name}
                                </span>
                                {!isCustomer && (
                                  <Badge className="rounded-full bg-rose-100 px-1.5 py-0 text-[9px] font-semibold text-rose-600">
                                    {t('common:x1.agent', 'Agent')}
                                  </Badge>
                                )}
                                {comment.is_internal && (
                                  <Badge className="rounded-full bg-amber-100 px-1.5 py-0 text-[9px] font-semibold text-amber-600">
                                    {t('views:f.sInternal', 'Intern')}
                                  </Badge>
                                )}
                                <span className="text-[10px] text-slate-400 ml-auto">
                                  {formatDayTime(comment.created_at)}
                                </span>
                              </div>
                              <div
                                className="text-xs text-slate-600 leading-relaxed prose prose-sm max-w-none"
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(comment.content || '')
                                }}
                              />
                            </div>
                          );
                        })
                      ) : (
                        <div className="text-center py-8 text-slate-400 text-sm">
                          {t('common:x2.noMessages', 'Keine Nachrichten vorhanden')}
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
                  {t('common:x2.ticketNotFound', 'Ticket nicht gefunden')}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-gray-500">{t('ticketList:state.loading', 'Lade Tickets...')}</div>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-red-500">
              {t('common:x2.ticketsLoadError', 'Fehler beim Laden der Tickets.')}
              <button
                onClick={() => refetch()}
                className="ml-2 text-blue-500 underline"
              >
                {t('common:action.retry', 'Erneut versuchen')}
              </button>
            </div>
          </div>
        )}

        {isSearchDialogOpen && (
          <div className="ticket-list-search-layer">
            <button
              type="button"
              className="ticket-list-search-backdrop"
              onClick={() => setIsSearchDialogOpen(false)}
              aria-label="Suche schließen"
            />
            <aside
              className="ticket-list-search-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="ticket-list-search-title"
            >
            <header className="ticket-list-search-header">
              <div>
                <h2 id="ticket-list-search-title">Tickets durchsuchen</h2>
                <p>
                  Ticketnummer, Betreff, Kontakt oder Inhalt
                </p>
              </div>
              <button
                type="button"
                className="ticket-list-search-close"
                onClick={() => setIsSearchDialogOpen(false)}
                aria-label="Suche schließen"
              >
                <X className="h-4 w-4" />
              </button>
            </header>

            <form
              className="ticket-list-search-form"
              onSubmit={(event) => {
                event.preventDefault();
                submitSearch();
              }}
            >
              <Search className="ticket-list-search-form-icon" />
              <Input
                ref={searchDialogInputRef}
                value={searchDraft}
                onChange={(event) => setSearchDraft(event.target.value)}
                className="ticket-list-search-input"
                placeholder="Ticket, Kunde oder Inhalt suchen …"
                autoComplete="off"
              />
              {isQuickSearching ? (
                <Loader2 className="ticket-list-search-loading" />
              ) : searchDraft ? (
                <button
                  type="button"
                  className="ticket-list-search-clear"
                  onClick={() => setSearchDraft('')}
                  aria-label="Suche leeren"
                >
                  <X className="h-4 w-4" />
                </button>
              ) : null}
            </form>

            <div className="ticket-list-search-results">
              {debouncedSearchDraft.length < 2 ? (
                <div className="ticket-list-search-empty">
                  <Search className="h-5 w-5" />
                  <div>
                    <strong>Direkt zum richtigen Ticket</strong>
                    <span>Mindestens zwei Zeichen eingeben.</span>
                  </div>
                </div>
              ) : isQuickSearching ? (
                <div className="ticket-list-search-empty">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <div>
                    <strong>Tickets werden gesucht</strong>
                    <span>Betreff, Kontakte und Nachrichten werden geprüft.</span>
                  </div>
                </div>
              ) : quickSearchTickets.length === 0 ? (
                <div className="ticket-list-search-empty">
                  <Search className="h-5 w-5" />
                  <div>
                    <strong>Keine Treffer</strong>
                    <span>Versuche es mit einer Ticketnummer oder einem anderen Begriff.</span>
                  </div>
                </div>
              ) : (
                quickSearchTickets.slice(0, 8).map((ticket) => (
                  <button
                    key={ticket.id}
                    type="button"
                    className="ticket-list-search-result"
                    onClick={() => openSearchTicket(ticket.id)}
                  >
                    <span className="ticket-list-search-result-mark" aria-hidden="true" />
                    <span className="ticket-list-search-result-content">
                      <strong>{ticket.subject}</strong>
                      <span>
                        {formatTicketNumber(ticket.ticketNumber)}
                        <i aria-hidden="true" />
                        {ticket.requester.name}
                        {ticket.organization?.name ? <><i aria-hidden="true" />{ticket.organization.name}</> : null}
                      </span>
                    </span>
                    <span className={getStatusColor(ticket.status)}>{getStatusLabel(ticket.status)}</span>
                    <ChevronRight className="ticket-list-search-result-arrow" />
                  </button>
                ))
              )}
            </div>

            <div className="ticket-list-search-footer">
              <span><kbd>ESC</kbd> Schließen</span>
              <button
                type="button"
                onClick={submitSearch}
                disabled={searchDraft.trim().length < 2}
              >
                Alle Ergebnisse anzeigen
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
            </aside>
          </div>
        )}

        {/* Status Change Dialog */}
        <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
          <DialogContent className="max-w-md rounded-3xl border border-slate-200/70 bg-white/95 shadow-xl backdrop-blur">
            <DialogHeader className="space-y-1">
              <DialogTitle>{t('ticketList:action.changeStatus', 'Status ändern')}</DialogTitle>
              <DialogDescription>
                {t('common:x2.statusFor', 'Status für')} {selectedTickets.length} ausgewählte Tickets ändern
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="rounded-full border border-slate-200 bg-white/80 text-sm focus:border-[#0F99FD]/40 focus:ring-[#0F99FD]/30">
                  <SelectValue placeholder={t('common:x2.selectNewStatus', 'Neuen Status auswählen')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">{t('domain:status.new', 'Neu')}</SelectItem>
                  <SelectItem value="open">{t('domain:status.open', 'Offen')}</SelectItem>
                  <SelectItem value="pending">{t('domain:status.pending', 'Wartend')}</SelectItem>
                  <SelectItem value="in_progress">{t('domain:status.in_progress', 'In Bearbeitung')}</SelectItem>
                  <SelectItem value="closed">{t('domain:status.closed', 'Geschlossen')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setStatusDialogOpen(false)}
                className="rounded-full border border-slate-200 px-4 py-1.5 text-sm font-medium text-slate-600 shadow-sm transition hover:border-[#0F99FD]/40 hover:text-[#0F99FD]"
              >
                {t('common:action.cancel', 'Abbrechen')}
              </Button>
              <Button onClick={handleStatusChange} disabled={!newStatus} className="rounded-full bg-[#0F99FD] px-4 py-1.5 text-sm font-semibold text-white transition hover:bg-[#0c84e0]">
                {t('ticketList:action.changeStatus', 'Status ändern')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assignment Dialog */}
        <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
          <DialogContent className="max-w-md rounded-3xl border border-slate-200/70 bg-white/95 shadow-xl backdrop-blur">
            <DialogHeader className="space-y-1">
              <DialogTitle>{t('ticketList:action.assignTickets', 'Tickets zuweisen')}</DialogTitle>
              <DialogDescription>
                {selectedTickets.length} ausgewählte Tickets zuweisen
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Select value={newAssignee} onValueChange={setNewAssignee}>
                <SelectTrigger>
                  <SelectValue placeholder={t('common:x2.selectUser', 'Benutzer auswählen')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">{t('ticketList:state.unassigned', 'Nicht zugewiesen')}</SelectItem>
                  {agentsResponse?.data?.agents?.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setAssignDialogOpen(false)}
                className="rounded-full border border-slate-200 px-4 py-1.5 text-sm font-medium text-slate-600 shadow-sm transition hover:border-[#0F99FD]/40 hover:text-[#0F99FD]"
              >
                {t('common:action.cancel', 'Abbrechen')}
              </Button>
              <Button onClick={handleAssignChange} disabled={!newAssignee} className="rounded-full bg-[#0F99FD] px-4 py-1.5 text-sm font-semibold text-white transition hover:bg-[#0c84e0]">
                {t('ticketList:action.assign', 'Zuweisen')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* Spam Confirmation Dialog */}
        <Dialog open={spamDialogOpen} onOpenChange={setSpamDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('ticketList:action.moveToArchive', 'Als Spam markieren')}</DialogTitle>
              <DialogDescription>
                {t('ticketList:action.spam_bulkQuestion', '{{count}} ausgewählte Tickets als Spam markieren? Alle weiteren E-Mails dieser Absender landen künftig automatisch im Spam.', { count: selectedTickets.length })}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setSpamDialogOpen(false)}
              >
                {t('common:action.cancel', 'Abbrechen')}
              </Button>
              <Button variant="destructive" onClick={handleMarkAsSpam}>{t('ticketList:action.archive', 'Spam')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Ticket Side Panel */}
        <Dialog open={isCreateTicketOpen} onOpenChange={setIsCreateTicketOpen}>
          <DialogContent className="ticket-create-panel">
            <DialogHeader className="ticket-create-header">
              <span className="ticket-create-eyebrow">
                {ticketMode === 'intern' ? 'Interner Workflow' : 'Kundenanfrage'}
              </span>
              <DialogTitle>
                {ticketMode === 'intern' ? 'Interne Konversation' : 'Neues Ticket'}
              </DialogTitle>
              <DialogDescription>
                {ticketMode === 'intern'
                  ? 'Eine direkte Anfrage zwischen zwei Organisationen starten.'
                  : 'Kundendaten und Ticketdetails kompakt erfassen.'}
              </DialogDescription>
            </DialogHeader>

            <div className="ticket-create-mode-switch" role="tablist" aria-label="Ticket-Typ">
              <button
                type="button"
                role="tab"
                aria-selected={ticketMode === 'extern'}
                onClick={() => setTicketMode('extern')}
                className={ticketMode === 'extern' ? 'is-active' : ''}
              >
                {t('common:x2.externalCustomer', 'Extern (Kunde)')}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={ticketMode === 'intern'}
                onClick={() => setTicketMode('intern')}
                className={ticketMode === 'intern' ? 'is-active' : ''}
              >
                {t('ticket:ui.internalConv', 'Intern')}
              </button>
            </div>

            <div className="ticket-create-body">
              {ticketMode === 'intern' && (
                <div className="ticket-create-note">
                  <span>Ohne E-Mail</span>
                  Die Ziel-Organisation sieht das Ticket sofort in ihrer Liste.
                </div>
              )}

              {ticketMode === 'extern' && (
                <div className="ticket-create-section">
                  <div className="ticket-create-section-heading">
                    <strong>Kunde</strong>
                    <span>Kontaktinformationen</span>
                  </div>

                  <div className="ticket-create-field">
                    <Label htmlFor="ticket-email">E-Mail <em>*</em></Label>
                    <div className="ticket-create-input-with-action">
                      <Input
                        id="ticket-email"
                        type="email"
                        value={ticketForm.email}
                        onChange={(e) => handleTicketFormChange('email', e.target.value)}
                        placeholder={t('shell:ph.customerEmail', 'kunde@beispiel.de')}
                        required
                      />
                      <Popover open={isContactsPopoverOpen} onOpenChange={setIsContactsPopoverOpen}>
                        <PopoverTrigger asChild>
                          <button type="button" className="ticket-create-contact-trigger" aria-label="Kontakt auswählen">
                            <BookUser className="h-4 w-4" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="ticket-create-contact-popover w-80 p-0" align="end">
                          <div className="ticket-create-contact-search">
                            <Search className="h-4 w-4" />
                            <Input
                              placeholder={t('common:x1.searchContact', 'Kontakt suchen...')}
                              value={contactSearchQuery}
                              onChange={(e) => setContactSearchQuery(e.target.value)}
                            />
                          </div>
                          <div className="ticket-create-contact-list">
                            {filteredContacts.length === 0 ? (
                              <div className="ticket-create-contact-empty">
                                {contactSearchQuery ? 'Keine Kontakte gefunden' : 'Keine Kontakte vorhanden'}
                              </div>
                            ) : filteredContacts.map((contact) => (
                              <button
                                key={contact.id}
                                type="button"
                                onClick={() => {
                                  handleTicketFormChange('email', contact.email);
                                  if (!ticketForm.customerName) handleTicketFormChange('customerName', contact.name);
                                  setIsContactsPopoverOpen(false);
                                  setContactSearchQuery('');
                                }}
                                className="ticket-create-contact-item"
                              >
                                <span><BookUser className="h-3.5 w-3.5" /></span>
                                <div>
                                  <strong>{contact.name}</strong>
                                  <small>{contact.email}</small>
                                </div>
                              </button>
                            ))}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>

                  <div className="ticket-create-field">
                    <Label htmlFor="ticket-customerName">{t('ticket:section.customerName', 'Kundenname')}</Label>
                    <Input
                      id="ticket-customerName"
                      value={ticketForm.customerName}
                      onChange={(e) => handleTicketFormChange('customerName', e.target.value)}
                      placeholder={t('shell:ph.fullName', 'Max Mustermann')}
                    />
                  </div>
                </div>
              )}

              <div className="ticket-create-section">
                <div className="ticket-create-section-heading">
                  <strong>{ticketMode === 'intern' ? 'Verbindung' : 'Ticketdetails'}</strong>
                  <span>{ticketMode === 'intern' ? 'Quelle und Ziel festlegen' : 'Einordnung und Inhalt'}</span>
                </div>

                <div className={ticketMode === 'intern' ? 'ticket-create-field-grid' : ''}>
                  <div className="ticket-create-field">
                    <Label htmlFor="ticket-organization">
                      {ticketMode === 'intern' ? 'Von (Quelle)' : 'Organisation'} <em>*</em>
                    </Label>
                    <Select
                      value={ticketForm.organizationId}
                      onValueChange={(value) => handleTicketFormChange('organizationId', value)}
                    >
                      <SelectTrigger id="ticket-organization">
                        <SelectValue placeholder={t('shell:ph.org', 'Organisation auswählen')} />
                      </SelectTrigger>
                      <SelectContent className="ticket-create-select-content">
                        {orgsResponse?.data?.map((org) => (
                          <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {ticketMode === 'intern' && (
                    <div className="ticket-create-field">
                      <Label>{t('common:x2.toTargetReq', 'An (Ziel)')} <em>*</em></Label>
                      <Select value={internalTargetOrgId} onValueChange={setInternalTargetOrgId}>
                        <SelectTrigger>
                          <SelectValue placeholder="Ziel auswählen" />
                        </SelectTrigger>
                        <SelectContent className="ticket-create-select-content">
                          {orgsResponse?.data?.filter((org) => org.id !== ticketForm.organizationId).map((org) => (
                            <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                <div className="ticket-create-field-grid">
                  <div className="ticket-create-field">
                    <Label htmlFor="ticket-priority">{t('ticketList:column.priority', 'Priorität')}</Label>
                    <Select
                      value={ticketForm.priority}
                      onValueChange={(value) => handleTicketFormChange('priority', value)}
                    >
                      <SelectTrigger id="ticket-priority"><SelectValue /></SelectTrigger>
                      <SelectContent className="ticket-create-select-content">
                        <SelectItem value="low">{t('domain:priority.low', 'Niedrig')}</SelectItem>
                        <SelectItem value="normal">{t('domain:priority.normal', 'Normal')}</SelectItem>
                        <SelectItem value="high">{t('domain:priority.high', 'Hoch')}</SelectItem>
                        <SelectItem value="urgent">{t('domain:priority.urgent', 'Dringend')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="ticket-create-field ticket-create-field--wide">
                    <Label htmlFor="ticket-subject">{t('ticket:ui.subjectReq', 'Betreff')} <em>*</em></Label>
                    <Input
                      id="ticket-subject"
                      value={ticketForm.subject}
                      onChange={(e) => handleTicketFormChange('subject', e.target.value)}
                      placeholder={t('shell:ph.subject', 'Kurzer, eindeutiger Betreff')}
                      required
                    />
                  </div>
                </div>

                {ticketMode === 'intern' && (
                  <div className="ticket-create-field">
                    <Label htmlFor="ticket-internal-message">{t('warehouse:dialog.message', 'Nachricht')}</Label>
                    <Textarea
                      id="ticket-internal-message"
                      value={internalMessage}
                      onChange={(e) => setInternalMessage(e.target.value)}
                      rows={5}
                      placeholder={t('common:x2.firstMsgToTarget', 'Erste Nachricht an die Ziel-Organisation…')}
                    />
                  </div>
                )}
              </div>
            </div>

            <DialogFooter className="ticket-create-footer">
              <Button
                variant="ghost"
                onClick={handleCancelTicket}
                disabled={createTicketMutation.isPending}
              >
                {t('common:action.cancel', 'Abbrechen')}
              </Button>
              <Button
                onClick={handleSendTicket}
                className="ticket-create-submit"
                disabled={
                  (ticketMode === 'intern'
                    ? (!ticketForm.subject || !ticketForm.organizationId || !internalTargetOrgId)
                    : (!ticketForm.email || !ticketForm.subject || !ticketForm.organizationId)
                  ) || createTicketMutation.isPending}
              >
                {createTicketMutation.isPending ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> {t('common:state.creating', 'Wird erstellt...')}</>
                ) : t('common:x1.createTicket', 'Ticket erstellen')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Merge Tickets Modal */}
        <Dialog open={isMergeModalOpen} onOpenChange={setIsMergeModalOpen}>
          <DialogContent className="max-w-2xl bg-white">
            <DialogHeader>
              <DialogTitle>{t('ticketList:action.mergeTickets', 'Tickets zusammenführen')}</DialogTitle>
              <DialogDescription>
                Wählen Sie das Haupt-Ticket aus, zu dem die anderen Tickets zusammengeführt werden sollen.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Selected Tickets Info */}
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-2">
                  {t('common:x2.selectedTicketsCount', 'Ausgewählte Tickets (')}{selectedTickets.length})
                </h3>
                <div className="space-y-2">
                  {selectedTickets.map((ticketId) => {
                    const ticket = tickets?.find((t: any) => t.id === ticketId);
                    return (
                      <div
                        key={ticketId}
                        className={`flex items-center justify-between rounded-md border p-3 transition ${selectedParentTicketId === ticketId
                          ? 'border-[#0F99FD] bg-red-50'
                          : 'border-slate-200 bg-white'
                          }`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm text-slate-900">
                              {ticket?.ticketNumber}
                            </span>
                            {selectedParentTicketId === ticketId && (
                              <Badge className="bg-[#0F99FD] text-white text-xs">
                                {t('common:x1.mainTicket', 'Haupt-Ticket')}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-600 mt-1">
                            {ticket?.subject}
                          </p>
                          {ticket?.updatedAt && (
                            <p className="text-xs text-slate-400 mt-1">
                              {t('common:x1.ticketProcessing', 'Ticketbearbeitung:')} {formatDateTime(ticket.updatedAt)}
                            </p>
                          )}
                        </div>
                        <Button
                          variant={selectedParentTicketId === ticketId ? "default" : "outline"}
                          size="sm"
                          onClick={() => setSelectedParentTicketId(ticketId)}
                          className={selectedParentTicketId === ticketId ? "bg-[#0F99FD] hover:bg-[#0c84e0]" : ""}
                        >
                          {selectedParentTicketId === ticketId ? "Ausgewählt" : "Als Haupt wählen"}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Info Box */}
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                <div className="flex gap-3">
                  <div className="text-blue-600">ℹ️</div>
                  <div className="text-sm text-blue-900">
                    <p className="font-semibold mb-1">{t('ticketList:merge.whatHappens', 'Was passiert beim Zusammenführen?')}</p>
                    <ul className="list-disc list-inside space-y-1 text-blue-800">
                      <li>{t('common:x2.othersLinkedToMain', 'Die anderen Tickets werden zum Haupt-Ticket verknüpft')}</li>
                      <li>{t('common:x2.linkedAutoClosed', 'Verknüpfte Tickets werden automatisch geschlossen')}</li>
                      <li>{t('ticketList:merge.hint1', 'Sie erscheinen nicht mehr in der Ticket-Liste')}</li>
                      <li>{t('ticketList:merge.hint2', 'Sie können im Haupt-Ticket eingesehen werden')}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsMergeModalOpen(false);
                  setSelectedParentTicketId(null);
                }}
              >
                {t('common:action.cancel', 'Abbrechen')}
              </Button>
              <Button
                disabled={!selectedParentTicketId || mergeMutation.isPending}
                onClick={() => {
                  if (!selectedParentTicketId) return;
                  const childTickets = selectedTickets.filter(id => id !== selectedParentTicketId);
                  mergeMutation.mutate({
                    parentTicketId: selectedParentTicketId,
                    childTicketIds: childTickets
                  });
                }}
                className="bg-[#0F99FD] hover:bg-[#0c84e0]"
              >
                {mergeMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('common:x2.merging', 'Zusammenführen...')}
                  </>
                ) : (
                  <>
                    <Merge className="h-4 w-4 mr-2" />
                    {t('ticketList:action.merge', 'Zusammenführen')}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};

export default TicketList;
